/* ──────────────────────────────────────────────
   src/components/AsignaturaDialog.tsx
   Diálogo estandarizado para crear/editar asignaturas
   ────────────────────────────────────────────── */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import type { DialogProps } from '@mui/material/Dialog';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Asignatura } from '../hooks/useAsignaturas';
import { useTipos } from '../hooks/useTiposAsignatura';
import type { SimpleFormDialogProps } from './dialogs/types';

type Props = SimpleFormDialogProps<Asignatura, Omit<Asignatura, 'id' | 'tipo'>> &
  Omit<DialogProps, 'open' | 'onClose' | 'children'>;

export default function AsignaturaDialog({
  open,
  initial,
  onClose,
  onSave,
  readOnly = false,
  ...dialogProps
}: Props) {
  const { t } = useTranslation<'translation'>();
  const [submitting, setSubmitting] = useState(false);
  const { rows: tipos } = useTipos();

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<Omit<Asignatura, 'id' | 'tipo'>>({
    defaultValues: {
      nombre: '',
      activo: true,
      tipoId: null,
      noRequiereAlumno: false,
      excluirInformesFirmas: false,
    },
  });

  useEffect(() => {
    if (initial) {
      reset({ ...initial });
    } else {
      reset({
        nombre: '',
        activo: true,
        tipoId: null,
        noRequiereAlumno: false,
        excluirInformesFirmas: false,
      });
    }
  }, [initial, reset, open]);

  const activo = watch('activo');

  const submit = handleSubmit(async (data) => {
    if (readOnly) return onClose();

    setSubmitting(true);
    try {
      await onSave(data);
      onClose();
    } catch (err: any) {
      const raw = err?.response?.data?.message;
      const msg = Array.isArray(raw)
        ? raw.join('\n')
        : (raw ?? err?.message ?? t('common.unexpectedError'));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" {...dialogProps}>
      <DialogTitle>
        {initial
          ? t('asignaturaDialog.editTitle', { defaultValue: 'Editar asignatura' })
          : t('asignaturaDialog.newTitle', { defaultValue: 'Nueva asignatura' })}
      </DialogTitle>

      <form onSubmit={submit}>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t('asignaturaDialog.name', { defaultValue: 'Nombre' })}
              fullWidth
              disabled={readOnly || submitting}
              error={!!errors.nombre}
              helperText={errors.nombre?.message}
              {...register('nombre', {
                required:
                  t('common.required', { defaultValue: 'Campo requerido' }) ?? 'Campo requerido',
              })}
            />

            <Controller
              name="tipoId"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth disabled={readOnly || submitting}>
                  <InputLabel id="tipoLbl">
                    {t('asignaturaDialog.type', { defaultValue: 'Tipo' })}
                  </InputLabel>
                  <Select
                    labelId="tipoLbl"
                    label={t('asignaturaDialog.type', { defaultValue: 'Tipo' })}
                    {...field}
                    value={field.value ?? ''}
                  >
                    <MenuItem value="">
                      <em>{t('asignaturaDialog.noType', { defaultValue: 'Sin tipo' })}</em>
                    </MenuItem>
                    {tipos.map((tipo) => (
                      <MenuItem key={tipo.id} value={tipo.id}>
                        {tipo.codigo} — {tipo.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={activo}
                  disabled={readOnly || submitting}
                  {...register('activo')}
                />
              }
              label={t('asignaturaDialog.active', { defaultValue: 'Activo' })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={watch('noRequiereAlumno') ?? false}
                  disabled={readOnly || submitting}
                  {...register('noRequiereAlumno')}
                />
              }
              label={t('asignaturaDialog.noRequireStudent', {
                defaultValue: 'No requiere alumno',
              })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={watch('excluirInformesFirmas') ?? false}
                  disabled={readOnly || submitting}
                  {...register('excluirInformesFirmas')}
                />
              }
              label={t('subjects.table.excludeSignReports')}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel', { defaultValue: 'Cancelar' })}
          </Button>
          {!readOnly && (
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting
                ? t('common.saving', { defaultValue: 'Guardando...' })
                : initial
                  ? t('asignaturaDialog.save', { defaultValue: 'Guardar' })
                  : t('asignaturaDialog.create', { defaultValue: 'Crear' })}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
